import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { AnalyticsEvents, analytics } from "common/analytics";
import { useTranslation } from "common/locales";
import { Field, useFields, useUpdateField } from "model";
import { useMemo, useState } from "react";
import { useUIStore } from "stores";

export default function AssignFieldDialog({
  animalGroupId,
  open,
  close,
  onAssign,
}: AssignFieldDialogProps) {
  const [field, setField] = useState<Field | null>(null);
  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fields = useMemo(
    () => allFields.filter((field) => field.animalGroup !== animalGroupId),
    [allFields, animalGroupId]
  );
  const { t } = useTranslation();

  // Use React Query mutation for field updates
  const updateFieldMutation = useUpdateField();

  // Get onboarding completion dialog trigger
  const openOnboardingCompletionDialog = useUIStore(
    (state) => state.openOnboardingCompletionDialog
  );

  // Check if any field currently has an animal group assigned (before this assignment)
  const hasAnyAssignedField = useMemo(
    () =>
      allFields.some(
        (f) => f.animalGroup !== null && f.animalGroup !== undefined
      ),
    [allFields]
  );

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("assignFieldDialog.title")}</DialogTitle>
      <DialogContent>
        <Autocomplete
          disabled={fields.length === 0}
          options={fields}
          getOptionLabel={(field) => field.name}
          style={{ width: "100%" }}
          onChange={(_, newValue) => setField(newValue)}
          value={field}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(
                fields.length === 0
                  ? "assignFieldDialog.nofieldsLabel"
                  : "assignFieldDialog.inputLabel"
              )}
              variant="outlined"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t("settings.button.close")}
        </Button>
        <Button disabled={fields.length === 0} onClick={assign} color="primary">
          {t("assignFieldDialog.button")}
        </Button>
      </DialogActions>
    </Dialog>
  );

  function onClose() {
    setField(null);
    close();
  }

  function assign() {
    if (!field) return;
    analytics.track(AnalyticsEvents.ANIMAL_GROUP_FIELD_ASSIGNED);
    const isFirstAssignment = !hasAnyAssignedField;
    const assignedField = field;
    updateFieldMutation.mutate(
      { ...field, animalGroup: animalGroupId },
      {
        onSuccess: () => {
          setField(null);
          close();
          if (isFirstAssignment) {
            openOnboardingCompletionDialog();
          }
          onAssign?.(assignedField);
        },
      }
    );
  }
}

type AssignFieldDialogProps = {
  open: boolean;
  close: () => void;
  animalGroupId: string;
  onAssign?: (field: Field) => void;
};
